import { AudioDevicesInfo, BizResult, Ok } from "@/models/biz_model"
import { KCSDK } from "@/sdk"
import { AudioInputCache } from "@/utils/storage"
import { ConnectionUseCase } from "./connection"
import { DataReportUsecase } from "./data_report"
import { ERR_NO_MIC_PERMISSION, ERR_OPEN_MIC } from "@/models/constants"
import { useEffect } from "react"

export type OnAudioDeviceInfo = (deviceInfo: AudioDevicesInfo) => void
export let audioDevInfo: AudioDevicesInfo | null
type AudioInputStateListener = (isOpen: boolean) => void
const audioInputStateListeners = new Set<AudioInputStateListener>()
export interface AudioInputOperationEvent {
  isOpen: boolean
  phase: "start" | "success" | "error"
}
const audioInputOperationListeners = new Set<
  (event: AudioInputOperationEvent) => void
>()

function notifyAudioInputState(isOpen: boolean): void {
  audioInputStateListeners.forEach((listener) => listener(isOpen))
}

function addAudioInputStateListener(
  listener: AudioInputStateListener
): () => void {
  audioInputStateListeners.add(listener)
  return () => audioInputStateListeners.delete(listener)
}

function notifyAudioInputOperation(event: AudioInputOperationEvent): void {
  audioInputOperationListeners.forEach((listener) => listener(event))
}

function addAudioInputOperationListener(
  listener: (event: AudioInputOperationEvent) => void
): () => void {
  audioInputOperationListeners.add(listener)
  return () => audioInputOperationListeners.delete(listener)
}

/**
 * 开始获取音频流，在连接上云桌面后调用
 *
 * @param callback 获取到音频设备信息的回调
 */
export function startAudio(callback: OnAudioDeviceInfo): void {
  const empty: AudioDevicesInfo = {
    outputs: [],
    inputs: [],
    outputVolume: 100,
    inputVolume: 100,
  }
  if (!navigator.mediaDevices?.enumerateDevices) {
    audioDevInfo = empty
    callback(audioDevInfo)
    return
  }
  void navigator.mediaDevices
    .enumerateDevices()
    .then((devices) => {
      audioDevInfo = {
        outputs: devices
          .filter((device) => device.kind === "audiooutput")
          .map((device, index) => ({
            index,
            name: device.label || `音频输出设备 ${index + 1}`,
          })),
        inputs: devices
          .filter((device) => device.kind === "audioinput")
          .map((device, index) => ({
            index,
            name: device.label || `麦克风 ${index + 1}`,
          })),
        outputVolume: 100,
        inputVolume: 100,
      }
      callback(audioDevInfo)
    })
    .catch((error) => {
      console.error("Start audio error: ", error)
      audioDevInfo = empty
      callback(audioDevInfo)
    })
}

/**
 * 停止获取音频流，在断开云桌面连接后调用
 */
export function stopAudio(): void {
  audioDevInfo = null
}

function getAudioInputConstraints(
  inputDevice: MediaDeviceInfo | null
): boolean | MediaTrackConstraints {
  if (!inputDevice?.deviceId || inputDevice.deviceId === "default") return true
  return { deviceId: { exact: inputDevice.deviceId } }
}

async function getAudioInputMediaStream(
  inputDevice: MediaDeviceInfo | null
): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: getAudioInputConstraints(inputDevice),
    })
  } catch (error) {
    if (inputDevice === null || inputDevice.deviceId === "default") throw error
    console.warn("Open selected microphone failed, retry default: ", error)
    return await navigator.mediaDevices.getUserMedia({ audio: true })
  }
}

/**
 * 切换音频输入输出设备
 *
 * @param inputDev 输入设备 index
 * @param outputDev 输出设备 index
 * @returns
 */
export async function selectDevice(
  inputDev: number,
  outputDev: number
): Promise<BizResult<number>> {
  return new Ok(0)
}

/**
 * 设置是否启用音频输出设备
 *
 * @param enable
 * @returns
 */
export function setAudioOutputEnable(enable: boolean): BizResult<number> {
  return new Ok(0)
}

// 返回缓存的音频输入状态
function isAudioInputEnabled(): boolean {
  const deviceId = ConnectionUseCase.getConnectedDevice()?.id
  if (deviceId === undefined) {
    return false
  }
  return AudioInputCache.getAudioInputEnable(deviceId)
}

/**
 * 设置输出设备音量
 *
 * @param volume 音量值，范围 0-100
 */
export function setOutputVolume(volume: number): BizResult<number> {
  return new Ok(0)
}

/**
 * 设置输入设备音量
 *
 * @param volume 音量值，范围 0-100
 */
export function setInputVolumn(): BizResult<number> {
  return new Ok(0)
}

// 音频输入流
let audioInputStream: MediaStream | null = null
let audioInputClosing: Promise<void> | null = null

async function getPreferredAudioInputDevice(): Promise<MediaDeviceInfo | null> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return null
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const audioInputDevices = devices.filter(
      (device) => device.kind === "audioinput"
    )
    const defaultDevice = audioInputDevices.find(
      (device) => device.deviceId === "default"
    )

    // 优先使用系统默认麦克风；没有 default 时使用枚举到的第一个麦克风
    return defaultDevice ?? audioInputDevices[0] ?? null
  } catch (e) {
    console.error("Get audio input devices error: ", e)
    return null
  }
}

// 打开音频输入流
async function openAudioInputStream(): Promise<boolean> {
  // 重连时可能仍在异步释放上一条音频轨道，必须等待释放完成后再向新连接添加轨道。
  if (audioInputClosing !== null) {
    await audioInputClosing
  }

  const deviceId = ConnectionUseCase.getConnectedDevice()?.id
  if (deviceId === undefined) {
    notifyAudioInputState(false)
    return false
  }

  if (audioInputStream !== null) {
    // 重连后 SDK 可能已经换了 PeerConnection；即使本地 MediaStream 仍在，
    // 也要重新绑定到新的音频发送轨道。SDK 在原连接仍有效时会自动去重。
    try {
      await Promise.resolve(KCSDK.enableAudioInput(audioInputStream))
      AudioInputCache.saveAudioInputEnable(deviceId, true)
      notifyAudioInputState(true)
      return true
    } catch (error) {
      console.error("Rebind audio input stream error: ", error)
      notifyAudioInputState(false)
      return false
    }
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    console.error("Open input audio stream error: getUserMedia not supported")
    notifyAudioInputState(false)
    return false
  }

  try {
    const inputDevice = await getPreferredAudioInputDevice()
    const stream = await getAudioInputMediaStream(inputDevice)

    // 调用 SDK 开启音频输入
    audioInputStream = stream
    await Promise.resolve(KCSDK.enableAudioInput(stream))
    // 缓存音频输入状态
    AudioInputCache.saveAudioInputEnable(deviceId, true)
    notifyAudioInputState(true)
  } catch (e) {
    console.error("Open input audio stream error: ", e)
    // 区分权限拒绝与其他开启失败，对齐 android ErrCode.noMicPermission/openMic
    if (e instanceof DOMException && (e.name === "NotAllowedError" || e.name === "SecurityError")) {
      DataReportUsecase.reportError(ERR_NO_MIC_PERMISSION, "无权限使用麦克风")
    } else {
      DataReportUsecase.reportError(ERR_OPEN_MIC, "麦克风开启失败")
    }
    // 授权失败
    audioInputStream?.getTracks().forEach((track) => track.stop())
    audioInputStream = null
    AudioInputCache.saveAudioInputEnable(deviceId, false)
    notifyAudioInputState(false)
    return false
  }

  return true
}

// 获取音频输入设备列表
async function getAudioInputDevices(): Promise<MediaDeviceInfo[] | null> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return null
  }

  const devices = await navigator.mediaDevices.enumerateDevices()
  return devices.filter((device) => device.kind === "audioinput")
}

/**
 * 关闭音频输入流
 *
 * @param isMarkDisable 是否标记为禁用状态
 */
async function closeAudioInputStream(isMarkDisable = true): Promise<void> {
  // 设置音频输入状态缓存
  const deviceId = ConnectionUseCase.getConnectedDevice()?.id
  if (isMarkDisable && deviceId !== undefined) {
    AudioInputCache.saveAudioInputEnable(deviceId, false)
  }

  if (audioInputStream === null) {
    if (audioInputClosing !== null) {
      await audioInputClosing
    }
    // 音频流未开启
    notifyAudioInputState(false)
    return
  }

  const stream = audioInputStream
  audioInputStream = null
  const closing = (async () => {
    const tracks = stream.getTracks()
    try {
      await Promise.resolve(KCSDK.disableAudioInput())
    } finally {
      tracks.forEach((track) => {
        if (track.readyState !== "ended") track.stop()
      })
      notifyAudioInputState(false)
    }
  })()
  audioInputClosing = closing
  try {
    await closing
  } finally {
    if (audioInputClosing === closing) audioInputClosing = null
  }
}

function getAudioInputStream(): MediaStream | null {
  return audioInputStream
}

function hasActiveAudioInputStream(): boolean {
  return (
    audioInputStream
      ?.getAudioTracks()
      .some((track) => track.readyState === "live") ?? false
  )
}

export const AudioUsecase = {
  isAudioInputEnabled,
  openAudioInputStream,
  closeAudioInputStream,
  getAudioInputStream,
  getAudioInputDevices,
  addAudioInputStateListener,
  addAudioInputOperationListener,
}

// 初始化音频输入流
export const useInitAudioInput = () => {
  useEffect(() => {
    const connectionCallback = {
      onDisconnect: () => {
        // 退出云桌面时断开音频输入流
        AudioUsecase.closeAudioInputStream(false)
      },
      // 音频流由 SDK 在新 PeerConnection 建立后统一恢复，避免在 RECONN
      // 通知阶段操作旧 sender 并与 clear()/媒体恢复产生竞态。
    }
    const agentMicCallback = {
      onMicStateChange: (isOpen: boolean) => {
        // Agent 在进入会议、重连恢复后会重发当前状态。若本地已经处于相同
        // 状态，不再重复调用打开/关闭逻辑和展示无意义的提示。
        if (isOpen && hasActiveAudioInputStream()) {
          return
        }
        if (!isOpen && audioInputStream === null) {
          return
        }
        notifyAudioInputOperation({ isOpen, phase: "start" })
        const operation = isOpen
          ? AudioUsecase.openAudioInputStream().then((success) => {
              notifyAudioInputOperation({
                isOpen,
                phase: success ? "success" : "error",
              })
              return success
            })
          : AudioUsecase.closeAudioInputStream().then(() => {
              notifyAudioInputOperation({ isOpen, phase: "success" })
            })
        operation.catch((error) => {
          notifyAudioInputOperation({ isOpen, phase: "error" })
          console.error("Handle agent microphone state change error: ", error)
        })
      },
    }
    ConnectionUseCase.addConnCallback(connectionCallback)
    ConnectionUseCase.addAgentMicStateCallback(agentMicCallback)

    if (isAudioInputEnabled()) {
      // 默认开启音频输入，尝试打开音频输入流
      AudioUsecase.openAudioInputStream()
    }
    return () => {
      ConnectionUseCase.removeConnCallback(connectionCallback)
      ConnectionUseCase.removeAgentMicStateCallback(agentMicCallback)
    }
  }, [])
}
